import React, { useCallback, useEffect, useState, useContext, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm, SubmitHandler } from "react-hook-form";
import axios, { AxiosError } from "axios";

import FormItem from "../../components/FormItem";
import Button from "../../components/Button";
import AppLogo from "../../components/AppLogo/AppLogo";
import InputErrorMessage from "../../components/InputErrorMessage";

import { AuthContext } from "../../contexts/AuthContext";
import { generateRequestConfig } from "../../constants";
import { getAppInfo, handleErrorForms, getDeviceId, getEmail, getInviteKey, referer, ensureCsrfToken, getPrompt } from "../../utils";
import { LogoutFormDataType } from "./Logout.constants";
import { resolveLogoutReturnUrl } from "../../logoutReturnUrl";
import { buildAuthCallbackUrl } from "../../authCallbackUrl";

type LogoutState = {
  generalErrors?: string;
  submitButton: boolean;
  appInfo: any;
  email?: string;
  inviteEmail?: string;
  inviteKey?: string;
};

const Logout: React.FC = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;

  const { AuthContextState, handleLogOut, handleLogIn, redirectToApp } = useContext(AuthContext);
  const { csrf_token, idApp, logout } = AuthContextState;

  const idempotentLogoutStartedRef = useRef(false);
  const activeSessionCheckStartedRef = useRef(false);
  const continueRequestStartedRef = useRef(false);
  const loginRedirectStartedRef = useRef(false);
  
  const [state, setState] = useState<LogoutState>({
    generalErrors: undefined,
    submitButton: false,
    appInfo: null,
    email: undefined,
    inviteEmail: undefined,
    inviteKey: undefined,
  });

  const { handleSubmit } = useForm<LogoutFormDataType>();

  const finishWithoutCoreSession = useCallback(async () => {
    if (idempotentLogoutStartedRef.current) return;
    idempotentLogoutStartedRef.current = true;
    const returnUrl = resolveLogoutReturnUrl(referer(), state.appInfo);
    if (!returnUrl) {
      setState((prev) => ({
        ...prev,
        generalErrors: "Error authorization.",
      }));
      idempotentLogoutStartedRef.current = false;
      return;
    }

    try {
      const csrfToken = await ensureCsrfToken(csrf_token);
      const requestConfig = generateRequestConfig(
        "put",
        "logout",
        {
          deviceId: getDeviceId(),
          appId: idApp,
        },
        {
          "X-CSRF-TOKEN": csrfToken,
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "Accept-Language": currentLang,
        }
      );
      const response = await axios(requestConfig);
      const logoutToken = response.data?.logout;
      if (!logoutToken) {
        throw new Error("Logout callback token is missing");
      }

      await handleLogOut(response.data);
      window.location.replace(
        buildAuthCallbackUrl(returnUrl, "logout", logoutToken)
      );
    } catch (error) {
      const errorAction = handleErrorForms(error);
      setState((prev) => ({
        ...prev,
        generalErrors: errorAction?.message || "Error authorization.",
      }));
      idempotentLogoutStartedRef.current = false;
    }
  }, [csrf_token, currentLang, handleLogOut, idApp, state.appInfo]);

  useEffect(() => {
    if (logout) redirectToApp();
    document.title = t("Logout");

    if (!state.appInfo) {
      getAppInfo((data) =>
        setState((prev) => ({ ...prev, appInfo: data }))
      );
      return;
    }

    // AuthContext functions are recreated when its state changes. Without
    // this guard, a successful one-click login can rerun this effect several
    // times while the browser is navigating to the consumer callback. Those
    // duplicate active-session checks may then start the logout fallback;
    // Safari reports the request cancelled by navigation as a CORS/network
    // error even though authentication has already succeeded.
    if (activeSessionCheckStartedRef.current) return;
    activeSessionCheckStartedRef.current = true;

    // docs/security-and-platform-work-plan.md Этап 7 — the access token is
    // an HttpOnly cookie now; ask the backend who (if anyone) is logged in
    // instead of decoding it client-side. No session (401) is the same
    // "nothing to log out of centrally" case the old `!token` branch used
    // to short-circuit on.
    let cancelled = false;
    axios(generateRequestConfig("get", "activesession", {}))
      .then((response) => {
        if (cancelled) return;

        // Silent SSO (prompt=none): an active session was found, so this
        // is exactly the "valid service callback" case -- skip the
        // "Continue?" click and drive the same flow that button triggers.
        if (getPrompt() === "none") {
          void handleContinue({ token: "" } as LogoutFormDataType);
          return;
        }

        setState((prev) => ({
          ...prev,
          email: response.data?.email,
          inviteEmail: getEmail() || undefined,
          inviteKey: getInviteKey() || undefined,
        }));
      })
      .catch(() => {
        if (cancelled || loginRedirectStartedRef.current) return;

        // Silent SSO (prompt=none): the session ProtectedRoute thought
        // was active didn't survive to this second check (rare
        // expiry/revocation race) -- a defined interaction-required
        // result, never the ordinary logout-fallback error message.
        if (getPrompt() === "none") {
          const ref = referer();
          if (ref) {
            window.location.replace(buildAuthCallbackUrl(ref, "error", "interaction_required"));
            return;
          }
        }

        void finishWithoutCoreSession();
      });

    return () => {
      cancelled = true;
    };
  }, [finishWithoutCoreSession, logout, redirectToApp, state.appInfo, t]);

  const handleLogout: SubmitHandler<LogoutFormDataType> = async (data) => {
    const csrfToken = await ensureCsrfToken(csrf_token);
    setState((prev) => ({
      ...prev,
      generalErrors: undefined,
      submitButton: true,
    }));

    const requestConfig = generateRequestConfig(
      "put",
      "logout",
      {
        deviceId: getDeviceId(),
        appId: idApp,
        ...data,
      },
      {
        "X-CSRF-TOKEN": csrfToken,
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
        "Accept-Language": currentLang,
      }
    );

    try {
      const res =  await axios(requestConfig);
      if (res.status === 200) {
        const returnUrl = resolveLogoutReturnUrl(referer(), state.appInfo);
        if (!returnUrl || !res.data?.logout) {
          throw new Error("Logout callback token is missing");
        }
        await handleLogOut(res.data);
        window.location.replace(
          buildAuthCallbackUrl(returnUrl, "logout", res.data.logout)
        );
      }

    } catch (error: AxiosError | any) {
      const errorAction = handleErrorForms(error);
      if (errorAction) {
        const { message, status } = errorAction;
        setState((prev) => ({ ...prev, generalErrors: message }));
        if (status === 401 || status === 404 || status === 500 || status === 200) {
          await finishWithoutCoreSession();
        }
      }
    } finally {
      setState((prev) => ({ ...prev, submitButton: false }));
    }
  };

  const handleContinue: SubmitHandler<LogoutFormDataType> = async (data) => {
    if (continueRequestStartedRef.current || loginRedirectStartedRef.current) return;
    continueRequestStartedRef.current = true;

    setState((prev) => ({
      ...prev,
      generalErrors: undefined,
      submitButton: true,
    }));

    try {
      const csrfToken = await ensureCsrfToken(csrf_token);
      const requestConfig = generateRequestConfig(
        "put",
        "oneclicklogin",
        {
          deviceId: getDeviceId(),
          appId: idApp,
          ...data,
        },
        {
          "X-CSRF-TOKEN": csrfToken,
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "Accept-Language": currentLang,
        }
      );

      const res = await axios(requestConfig);
      loginRedirectStartedRef.current = true;
      try {
        await handleLogIn(res.data);
      } catch (error) {
        loginRedirectStartedRef.current = false;
        throw error;
      }
    } catch (error) {
      if (loginRedirectStartedRef.current) return;
      const errorAction = handleErrorForms(error);
      if (errorAction) {
        const { message } = errorAction;
        setState((prev) => ({ ...prev, generalErrors: message }));
      }
    } finally {
      if (!loginRedirectStartedRef.current) {
        continueRequestStartedRef.current = false;
        setState((prev) => ({ ...prev, submitButton: false }));
      }
    }
  };

  const { generalErrors, submitButton, appInfo , email, inviteEmail, inviteKey  } = state;

  return (
    <>
      <h1 className="text-center auth-title-form">
        {t("Logout")}
        <AppLogo
          src={appInfo?.urlImage}
          name={appInfo?.name}
          frameClassName="auth-title-logo-frame"
          imageClassName="auth-title-logo"
          placeholderClassName="auth-title-logo-placeholder"
        />
      </h1>
      <div>

            {email == inviteEmail || inviteEmail==undefined ? (
              <>
                <div>
                  {t("An active ID.Express session has been detected for {{email}}. Would you like to [Continue] with this account or [Logout]?", { email: email || "" })}
                </div>
              </>
            ) : (
              <>
                <div>
                   {t("An active ID.Express session has been detected for {{email}}, which differs from the email used in the invitation. Please [Logout] to proceed with registration.", { email: email || "" })}
                </div>
              </>
            )}
       
      </div>
      <br/>
      <div className="card-body">
      {email == inviteEmail || inviteEmail==undefined  ? (
        <>
        <form name="AutoLogIn" onSubmit={handleSubmit(handleContinue)}>
          <Button
            type="submit"
            variant="light"
            width={100}
            height={48}
            disabled={submitButton}
            text={t("Continue")}
          />
        </form>

        <div className="p-1" />
        
        </> 
      ):(<></>)}

        <form name="Logout" onSubmit={handleSubmit(handleLogout)}>
          <FormItem>
            <Button
              type="submit"
              variant="default"
              width={100}
              height={48}
              disabled={submitButton}
              text={t("Logout")}
            />
            {generalErrors && <InputErrorMessage msg={t(generalErrors)} />}
          </FormItem>

          <div className="p-3 optext pb-5">
            {t("Continuing you agree with our")}{" "}
            <Link to={`/${currentLang}/terms_and_conditions/`}>
              {t("Terms and Conditions")}
            </Link>{" "}
            {t("and")}{" "}
            <Link to={`/${currentLang}/privacy_policy/`}>
              {t("Privacy Policy")}
            </Link>
            .
          </div>
        </form>
      </div>
    </>
  );
};

export default Logout;
